import { useEffect, useRef, useState } from "react";
import { X, CheckCircle2, Mail, Phone, MessageCircle } from "lucide-react";

export const WaitlistModal = ({ open, onClose, defaultRole = "Family" }) => {
  const [form, setForm] = useState({ name: "", phone: "", email: "", role: defaultRole });
  const [done, setDone] = useState(false);
  const nameRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    document.body.style.overflow = "hidden";
    nameRef.current?.focus();
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = "";
      window.removeEventListener("keydown", onKey);
    };
  }, [open, onClose]);

  if (!open) return null;

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const submit = (e) => { e.preventDefault(); setDone(true); };
  const close = () => { setDone(false); setForm({ name: "", phone: "", email: "", role: defaultRole }); onClose(); };

  const inputCls = "w-full rounded-xl border border-[#A8722A]/30 bg-[#FFFDF8] px-4 py-3 font-mukta text-[#3B221B] placeholder:text-[#6B5044]/60 focus:outline-none focus:ring-2 focus:ring-[#A8722A]/50";

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4" role="dialog" aria-modal="true" aria-label="Early access waitlist form">
      <div className="absolute inset-0 bg-[#3B221B]/60 backdrop-blur-sm" onClick={close} data-testid="waitlist-overlay" />
      <div className="relative w-full max-w-md rounded-[14px] border border-[#A8722A]/35 bg-[#FBF6EC] p-7 shadow-2xl" data-testid="waitlist-modal">
        <button onClick={close} aria-label="Close waitlist form" data-testid="waitlist-close-btn"
          className="absolute right-4 top-4 rounded-full p-1.5 text-[#6B5044] hover:bg-[#F3EADA] focus:outline-none focus:ring-2 focus:ring-[#A8722A]/50">
          <X size={18} />
        </button>
        {done ? (
          <div className="py-8 text-center" data-testid="waitlist-success">
            <CheckCircle2 className="mx-auto mb-4 text-[#3E6B4F]" size={44} strokeWidth={1.5} />
            <h3 className="font-eczar text-2xl font-semibold text-[#3B221B]">You're on the list!</h3>
            <p className="mt-2 font-mukta text-[#6B5044]">We'll notify you the moment bookings open in Bengaluru.</p>
            <p className="mt-4 font-baloo text-[#A8722A]">|| ಶುಭಮಸ್ತು ||</p>
          </div>
        ) : (
          <>
            <p className="font-baloo text-sm text-[#B23A2B]">|| ಶ್ರೀ ||</p>
            <h3 className="mt-1 font-eczar text-2xl font-semibold text-[#3B221B]">Get early access</h3>
            <p className="mt-1 font-mukta text-sm text-[#6B5044]">Launching soon in Bengaluru — join the list and be first in line.</p>
            <form onSubmit={submit} className="mt-5 space-y-3">
              <input required ref={nameRef} value={form.name} onChange={set("name")} placeholder="Your name" aria-label="Your name" className={inputCls} data-testid="waitlist-name-input" />
              <input required value={form.phone} onChange={set("phone")} type="tel" pattern="[0-9+ -]{8,15}" placeholder="Phone number" aria-label="Phone number" className={inputCls} data-testid="waitlist-phone-input" />
              <input required value={form.email} onChange={set("email")} type="email" placeholder="Email" aria-label="Email" className={inputCls} data-testid="waitlist-email-input" />
              <div className="flex gap-2" role="radiogroup" aria-label="I am a">
                {["Family", "Purohith"].map((r) => (
                  <button key={r} type="button" role="radio" aria-checked={form.role === r} data-testid={`waitlist-role-${r.toLowerCase()}`}
                    onClick={() => setForm((f) => ({ ...f, role: r }))}
                    className={`flex-1 rounded-xl border px-4 py-2.5 font-mukta text-sm transition-colors focus:outline-none focus:ring-2 focus:ring-[#A8722A]/50 ${form.role === r ? "border-[#B23A2B] bg-[#B23A2B] text-[#FFFDF8]" : "border-[#A8722A]/30 bg-[#FFFDF8] text-[#6B5044]"}`}>
                    {r}
                  </button>
                ))}
              </div>
              <button type="submit" data-testid="waitlist-submit-btn"
                className="w-full rounded-xl bg-[#A8722A] px-4 py-3 font-mukta font-semibold text-[#FFFDF8] transition-colors hover:bg-[#8f5f21] focus:outline-none focus:ring-2 focus:ring-[#A8722A]/50">
                Join the waitlist
              </button>
            </form>
            <div className="mt-4 border-t border-[#A8722A]/20 pt-3 text-center" data-testid="waitlist-help">
              <p className="font-mukta text-xs text-[#6B5044]">
                ಬುಕಿಂಗ್ ವೇಳೆ ಸಮಸ್ಯೆಯೇ? · Facing an issue or need info?
              </p>
              <div className="mt-1.5 flex flex-col items-center justify-center gap-1 text-xs sm:flex-row sm:gap-4">
                <a href="mailto:namaste@purohithconnect.com" className="inline-flex items-center gap-1.5 font-mukta text-[#B23A2B] hover:underline" data-testid="waitlist-help-email">
                  <Mail size={13} /> namaste@purohithconnect.com
                </a>
                <a href="tel:+919110248636" className="inline-flex items-center gap-1.5 font-mukta text-[#B23A2B] hover:underline" data-testid="waitlist-help-phone">
                  <Phone size={13} /> +91 91102 48636
                </a>
                <a href="https://wa.me/919110248636" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 font-mukta text-[#128C3E] hover:underline" data-testid="waitlist-help-whatsapp">
                  <MessageCircle size={13} /> WhatsApp
                </a>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};
